import { useState } from "react";

import { CalendarClock, Check } from "lucide-react";

import {
  FREQUENCY_OPTIONS,
  SCHEDULE_REPORT_TYPES,
  SCHEDULE_REPORT_TYPE_BY_VALUE,
  createSchedule,
} from "../utils/scheduleApi";
import LoadingSpinner from "./LoadingSpinner";
import UsageMeter from "./UsageMeter";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/Dialog";

const inputClasses =
  "w-full bg-surface-container-low border-none rounded-xl px-5 py-3.5 text-on-surface text-sm placeholder:text-outline/50 focus:ring-2 focus:ring-primary/20 focus:bg-surface-container-lowest transition-all outline-none";

const labelClasses =
  "block text-xs font-semibold text-on-surface-variant mb-1.5 uppercase tracking-wider";

// FastAPI returns `detail` as a string for HTTPExceptions, but as an array of
// error objects for 422 validation failures. Only surface it when it's a string.
const errorDetail = (err, fallback) => {
  const detail = err.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
};

// A metered type with none of its allowance left this period can't be scheduled.
const isBlockedByUsage = (usageItem) => {
  if (!usageItem) return false;
  if (!usageItem.included) return true;
  if (usageItem.unlimited) return false;
  return (usageItem.remaining ?? 0) <= 0;
};

/**
 * Central create-a-schedule modal: pick a report type, supply its target when required, choose a
 * cadence. Blocks types with no usage left this period.
 */
const ScheduleReportModal = ({
  open,
  onOpenChange,
  accountEmail = "",
  usageByFeature = null,
  defaultType = "audit",
  lockType = false,
  onCreated,
}) => {
  // When opened from a specific section the type is fixed to `defaultType` and the
  // picker is hidden; the top-level button opens with the picker (lockType false).
  const [pickedType, setPickedType] = useState(defaultType);
  const reportType = lockType ? defaultType : pickedType;
  const [target, setTarget] = useState("");
  const [frequency, setFrequency] = useState("weekly");
  const [secondEmail, setSecondEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const typeMeta = SCHEDULE_REPORT_TYPE_BY_VALUE[reportType] ?? SCHEDULE_REPORT_TYPES[0];
  const usageItem = typeMeta.usageFeature
    ? (usageByFeature?.[typeMeta.usageFeature] ?? null)
    : null;
  const blockedByUsage = isBlockedByUsage(usageItem);
  const targetRequired = Boolean(typeMeta.target?.required);

  const resetForm = () => {
    setError(null);
    setSuccess(false);
    setSecondEmail("");
    setTarget("");
    setFrequency("weekly");
    setPickedType(defaultType);
  };

  const handleClose = (val) => {
    if (!val) resetForm();
    onOpenChange(val);
  };

  const selectType = (value) => {
    setPickedType(value);
    setTarget("");
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // The server adds the account email as recipient 1; we only send the optional CC.
    const payload = { report_type: reportType, frequency };
    if (typeMeta.target) payload.target = target.trim();
    const cc = secondEmail.trim();
    if (cc) payload.second_email = cc;

    setIsSubmitting(true);
    try {
      await createSchedule(payload);
      setSuccess(true);
      onCreated?.();
      setTimeout(() => handleClose(false), 1200);
    } catch (err) {
      setError(errorDetail(err, "Failed to create schedule. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    Boolean(accountEmail) && !blockedByUsage && (!targetRequired || Boolean(target.trim()));

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Schedule report</DialogTitle>
          <DialogDescription>
            Get a fresh report emailed to you automatically on your chosen cadence.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <span className={labelClasses}>Report type</span>
              {lockType ? (
                <p className="text-sm font-semibold text-on-surface">{typeMeta.label}</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {SCHEDULE_REPORT_TYPES.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => selectType(t.value)}
                      className={`py-2.5 px-3 rounded-xl text-sm font-semibold transition-all ${
                        reportType === t.value
                          ? "bg-gradient-to-br from-primary to-primary-container text-on-primary"
                          : "bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
              {usageItem && <UsageMeter item={usageItem} className="mt-2" />}
            </div>

            {typeMeta.target && (
              <div>
                <label htmlFor="schedule-target" className={labelClasses}>
                  {typeMeta.target.label}
                </label>
                <input
                  id="schedule-target"
                  type="text"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  placeholder={typeMeta.target.placeholder}
                  className={inputClasses}
                />
              </div>
            )}

            <div>
              <span className={labelClasses}>Frequency</span>
              <div className="grid grid-cols-3 gap-2">
                {FREQUENCY_OPTIONS.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => setFrequency(f.value)}
                    className={`py-2.5 rounded-xl text-sm font-semibold transition-all ${
                      frequency === f.value
                        ? "bg-gradient-to-br from-primary to-primary-container text-on-primary"
                        : "bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="schedule-email" className={labelClasses}>
                Email
              </label>
              <input
                id="schedule-email"
                type="text"
                value={accountEmail}
                readOnly
                className={`${inputClasses} opacity-70`}
              />
            </div>

            <div>
              <label htmlFor="schedule-second-email" className={labelClasses}>
                CC (optional)
              </label>
              <input
                id="schedule-second-email"
                type="email"
                value={secondEmail}
                onChange={(e) => setSecondEmail(e.target.value)}
                placeholder="colleague@example.com"
                className={inputClasses}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !canSubmit}
              className="w-full bg-gradient-to-br from-primary to-primary-container text-on-primary py-3.5 rounded-xl font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2.5 disabled:opacity-70"
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="sm" /> Scheduling...
                </>
              ) : success ? (
                <>
                  <Check className="w-4 h-4" /> Scheduled!
                </>
              ) : (
                <>
                  <CalendarClock className="w-4 h-4" /> Schedule
                </>
              )}
            </button>

            {blockedByUsage && (
              <p className="text-on-surface-variant text-center text-xs">
                You have no {typeMeta.label} usage left this period, so it can't be scheduled right
                now.
              </p>
            )}
            {error && <p className="text-error text-center text-sm font-medium">{error}</p>}
          </form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleReportModal;
